import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Download, Eye, ImageIcon, FileType2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isImage = (name = '') => /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);
const isPdf = (name = '') => /\.pdf$/i.test(name);

function useAuthedImage(fileId, filename, publicUrl) {
  const [src, setSrc] = useState(publicUrl && publicUrl.startsWith('http') ? publicUrl : null);
  useEffect(() => {
    let revokeUrl = null;
    let cancelled = false;
    async function load() {
      if (publicUrl && publicUrl.startsWith('http')) return;
      if (!isImage(filename)) return;
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/files/${fileId}`, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` },
        });
        const url = URL.createObjectURL(res.data);
        revokeUrl = url;
        if (!cancelled) setSrc(url);
      } catch {
        /* silencioso */
      }
    }
    load();
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [fileId, filename, publicUrl]);
  return src;
}

function AttachmentThumb({ file, onOpen, onDownload, isComprobante }) {
  const src = useAuthedImage(file.id, file.original_filename, file.file_url);
  const img = isImage(file.original_filename);
  const pdf = isPdf(file.original_filename);
  return (
    <div
      className="group relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:shadow-lg transition-all"
      data-testid={`file-card-${file.id}`}
    >
      <button
        type="button"
        onClick={() => onOpen(file, src)}
        className="block w-full text-left"
        aria-label={`Abrir ${file.original_filename}`}
      >
        <div className="aspect-video bg-slate-50 dark:bg-slate-800/60 flex items-center justify-center overflow-hidden">
          {img && src ? (
            <img src={src} alt={file.original_filename} className="h-full w-full object-cover" loading="lazy" />
          ) : pdf ? (
            <div className="flex flex-col items-center text-slate-500 dark:text-slate-400">
              <FileType2 className="h-10 w-10 mb-1" />
              <span className="text-[10px] uppercase tracking-wider font-semibold">PDF</span>
            </div>
          ) : img ? (
            <ImageIcon className="h-8 w-8 text-slate-400 dark:text-slate-600" />
          ) : (
            <FileText className="h-8 w-8 text-slate-400 dark:text-slate-600" />
          )}
        </div>
      </button>
      <div className="p-3">
        <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate" title={file.original_filename}>
          {file.original_filename}
        </p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-[10px] text-slate-500 dark:text-slate-500">
            {(file.file_size / 1024).toFixed(1)} KB · {format(new Date(file.uploaded_at), 'dd MMM HH:mm', { locale: es })}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onOpen(file, src)}
              data-testid={`preview-file-${file.id}`}
              title="Vista previa"
            >
              <Eye className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onDownload(file.id, file.original_filename)}
              data-testid={`download-file-${file.id}`}
              title="Descargar"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        {isComprobante && (
          <Badge className="mt-2 bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 hover:bg-emerald-100">Comprobante</Badge>
        )}
      </div>
    </div>
  );
}

export default function AttachmentPreview({ files = [], comprobanteId, onDownload }) {
  const [open, setOpen] = useState(null); // { file, src }

  const handleOpen = (file, src) => setOpen({ file, src });
  const handleClose = () => setOpen(null);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {files.map((file) => (
          <AttachmentThumb
            key={file.id}
            file={file}
            onOpen={handleOpen}
            onDownload={onDownload}
            isComprobante={comprobanteId === file.id}
          />
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && handleClose()}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-sm text-slate-800 dark:text-slate-200 truncate">
              {open?.file?.original_filename}
            </DialogTitle>
          </DialogHeader>
          <div className="bg-slate-100 dark:bg-slate-900 rounded-lg overflow-hidden max-h-[75vh]">
            {open && isImage(open.file.original_filename) && open.src && (
              <img src={open.src} alt={open.file.original_filename} className="w-full h-auto object-contain max-h-[75vh]" />
            )}
            {open && isPdf(open.file.original_filename) && (
              <PdfViewer file={open.file} />
            )}
            {open && !isImage(open.file.original_filename) && !isPdf(open.file.original_filename) && (
              <div className="p-12 text-center text-slate-500 dark:text-slate-400">
                <FileText className="h-12 w-12 mx-auto mb-3" />
                <p className="text-sm">Vista previa no disponible. Descarga para ver el archivo.</p>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => onDownload(open.file.id, open.file.original_filename)}
              data-testid="dialog-download-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PdfViewer({ file }) {
  const [src, setSrc] = useState(file.file_url && file.file_url.startsWith('http') ? file.file_url : null);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    async function load() {
      if (file.file_url && file.file_url.startsWith('http')) return;
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/files/${file.id}`, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` },
        });
        const url = URL.createObjectURL(res.data);
        revoke = url;
        if (!cancelled) setSrc(url);
      } catch { /* ignore */ }
    }
    load();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [file]);
  if (!src) {
    return <div className="p-12 text-center text-sm text-slate-500 dark:text-slate-400">Cargando PDF...</div>;
  }
  return <iframe title={file.original_filename} src={src} className="w-full h-[75vh] bg-white" />;
}
